import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import type { RootState } from '../../store';
import {
  atualizarQuantidade,
  definirObservacaoGeral,
  duplicarItem,
  removerItem,
} from '../../store/carrinho-slice';
import { useGetCatalogoQuery, useGetLojaAtualQuery, type ProdutoCatalogo } from '../../api'
import { canalDoIdentificador, ehDispositivoFixo, ehVitrine } from '../../lib/canal';
import { formatarBRL } from '../../lib/formato';
import { useImagensPorProduto, useTiposCalculoPorPasso } from '../../lib/imagens-catalogo'
import { useReconciliacaoCarrinho } from '../../lib/usar-reconciliacao-carrinho';
import { avisoDeMudancaDePreco } from '../../lib/reconciliar-carrinho';
import { itensCarrinhoParaListagem } from '../../lib/itens-pedido';
import { sugerirAcompanhamentos } from '../../lib/sugestoes'
import { useAdicionarProdutoSimples } from '../../lib/use-adicionar-produto-simples'
import { ListaItensPedido } from '../../components/lista-itens-pedido';
import { PecaTambem } from '../../components/peca-tambem'
import { EstadoVazio } from '../../components/estado-mensagem';
import { CabecalhoModal } from '../../components/cabecalho-modal';
import { CarrinhoKiosk } from './kiosk';

export function CarrinhoPage(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: loja } = useGetLojaAtualQuery();
  const { itens, observacaoGeral } = useSelector((s: RootState) => s.carrinho);

  const subtotal = itens.reduce((s, item) => s + item.precoUnitario * item.quantidade, 0);
  const vazio = itens.length === 0;
  const bloqueado = !loja || ehVitrine(loja.identificador);
  const theme = useTheme();
  // Desktop: o carrinho abre como modal centralizado; mobile: tela cheia.
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Foto de cada item — o carrinho guarda só o id do produto.
  const canal = loja ? canalDoIdentificador(loja.identificador) : 'CARDAPIO_DELIVERY';
  const imagens = useImagensPorProduto(canal);
  const tiposCalculo = useTiposCalculoPorPasso(canal)
  const { alteracoes, descartarAviso } = useReconciliacaoCarrinho(canal);
  const aviso = avisoDeMudancaDePreco(alteracoes, formatarBRL);
  const itensListados = itensCarrinhoParaListagem(itens, imagens, tiposCalculo)

  // Mesma consulta do `useImagensPorProduto`: o RTK Query reaproveita o cache, sem requisição nova.
  const { data: catalogo } = useGetCatalogoQuery(canal)
  const adicionarProdutoSimples = useAdicionarProdutoSimples()
  const sugestoes = useMemo(
    () => (bloqueado ? [] : sugerirAcompanhamentos(catalogo?.grupos ?? [], itens, { somenteSemEscolha: true })),
    [bloqueado, catalogo, itens],
  )

  const adicionarSugestao = (produto: ProdutoCatalogo): void => {
    const grupo = catalogo?.grupos.find((g) => g.produtos.some((p) => p.id === produto.id))
    adicionarProdutoSimples(produto, { categoria: grupo?.descricao, lista: 'Peça também' })
  }

  const fechar = (): void => {
    navigate('/catalogo');
  };

  // Totem e tablet têm carrinho próprio, em tela cheia: o modal estreito de
  // celular quebrava a experiência no meio do pedido.
  if (loja && ehDispositivoFixo(loja.identificador)) {
    return (
      <CarrinhoKiosk
        bloqueado={bloqueado}
        alteracoes={alteracoes}
        aoDescartarAviso={descartarAviso}
        aoVoltar={() => navigate('/catalogo')}
        aoFinalizar={() => navigate('/checkout')}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={fechar}
      fullScreen={!ehDesktop}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          position: 'relative',
          // 90vh no Safari do iPad mede a viewport SEM a barra do navegador: o
          // rodapé do diálogo (onde fica o botão de ação) cai fora da tela.
          ...(ehDesktop ? { height: '90vh', '@supports (height: 90dvh)': { height: '90dvh' } } : {}),
        },
      }}
    >
      <CabecalhoModal titulo="Carrinho" aoFechar={fechar} />

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {vazio ? (
          <EstadoVazio
            icone={<ShoppingCartOutlinedIcon />}
            titulo="Seu carrinho está vazio"
            descricao="Escolha os itens no cardápio para começar seu pedido."
            acao={
              <Button variant="contained" onClick={() => navigate('/catalogo')}>
                Voltar ao cardápio
              </Button>
            }
          />
        ) : (
          <Stack spacing={1}>
            {aviso && (
              <Alert severity={aviso.tom} onClose={descartarAviso} sx={{ mb: 1 }}>
                {aviso.texto}
              </Alert>
            )}

            <ListaItensPedido
              itens={itensListados}
              acoes={{
                minimoUm: true,
                aoAlterarQuantidade: (id, delta) => {
                  const item = itens.find((i) => i.id === id);
                  if (item) dispatch(atualizarQuantidade({ id, quantidade: item.quantidade + delta }));
                },
                aoDefinirQuantidade: (id, quantidade) => dispatch(atualizarQuantidade({ id, quantidade })),
                aoDuplicar: (id) => dispatch(duplicarItem({ id })),
                aoRemover: (id) => dispatch(removerItem({ id })),
              }}
            />

            <PecaTambem produtos={sugestoes} aoAdicionar={adicionarSugestao} />

            <Box sx={{ pt: 0.5 }} />

            <TextField
              label="Observação geral do pedido (opcional)"
              value={observacaoGeral}
              onChange={(e) => dispatch(definirObservacaoGeral(e.target.value))}
              multiline
              fullWidth
              minRows={2}
            />
          </Stack>
        )}
      </DialogContent>

      {!vazio && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="h6">{formatarBRL(subtotal)}</Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/checkout')}
              disabled={bloqueado}
            >
              Finalizar pedido
            </Button>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
